import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

export interface DescriptionSection {
  subTitle: string;
  content: string[];
  additionalConclusion?: string;
}

// Friendly editor for a managed event's scheduling description — a list of
// sections (heading + bullet lines + optional closing line). Stored in the
// managed event's metadata as `schedulingDescription`.
export default function SchedulingDescriptionField({
  value,
  onChange,
}: {
  value: DescriptionSection[];
  onChange: (next: DescriptionSection[]) => void;
}) {
  const patch = (i: number, p: Partial<DescriptionSection>) =>
    onChange(value.map((s, idx) => (idx === i ? { ...s, ...p } : s)));
  const add = () =>
    onChange([...value, { subTitle: "", content: [], additionalConclusion: "" }]);
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Scheduling description{" "}
          <span className="text-gray-400">(shown on the booking screen)</span>
        </p>
        <Button type="button" size="sm" variant="outline" onClick={add}>
          <Plus className="h-4 w-4 mr-1" /> Add section
        </Button>
      </div>

      {value.length === 0 ? (
        <p className="text-xs text-gray-400">No sections yet.</p>
      ) : (
        value.map((section, i) => (
          <div
            key={i}
            className="rounded-md border border-gray-200 p-3 space-y-2 bg-white"
          >
            <div className="flex items-center justify-between">
              <Badge variant="outline">Section {i + 1}</Badge>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => remove(i)}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>

            <div>
              <p className="text-[11px] text-gray-500 mb-1">Heading</p>
              <Input
                className="h-8 text-sm"
                placeholder="e.g. What to expect"
                value={section.subTitle}
                onChange={(e) => patch(i, { subTitle: e.target.value })}
              />
            </div>

            <div>
              <p className="text-[11px] text-gray-500 mb-1">
                Bullets (one per line)
              </p>
              <textarea
                className="w-full min-h-[70px] rounded-md border border-gray-200 p-2 text-sm"
                placeholder={"50-minute video session\nBring any prior reports"}
                value={section.content.join("\n")}
                onChange={(e) =>
                  patch(i, { content: e.target.value.split("\n") })
                }
              />
            </div>

            <div>
              <p className="text-[11px] text-gray-500 mb-1">
                Closing line (optional, italic)
              </p>
              <Input
                className="h-8 text-sm"
                placeholder="e.g. You can reschedule up to 24h before"
                value={section.additionalConclusion ?? ""}
                onChange={(e) =>
                  patch(i, { additionalConclusion: e.target.value })
                }
              />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
